import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { companyUserService, CompanyUser, CompanyUserFilters, CompanyUserLimited } from '../../services/companyUserService';
import { invitationService, Invitation } from '../../services/invitationService';

interface CompanyUsersState {
  companyUsers: CompanyUser[];
  currentCompanyUser: CompanyUser | null;
  limitedUsers: CompanyUserLimited[];
  limitedUsersLastFetched: number | null; // Timestamp for cache validation
  isLoadingLimited: boolean;
  invitations: Invitation[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  filters: CompanyUserFilters;
  isLoading: boolean;
  isLoadingInvitations: boolean;
  error: string | null;
  stats: {
    totalUsers: number;
    activeUsers: number;
    byRole: Record<string, number>;
    byDepartment: Record<string, number>;
  } | null;
}

const initialState: CompanyUsersState = {
  companyUsers: [],
  currentCompanyUser: null,
  limitedUsers: [],
  limitedUsersLastFetched: null,
  isLoadingLimited: false,
  invitations: [],
  pagination: {
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  },
  filters: {},
  isLoading: false,
  isLoadingInvitations: false,
  error: null,
  stats: null
};

// Async thunks
export const fetchCompanyUsers = createAsyncThunk(
  'companyUsers/fetchCompanyUsers',
  async (filters?: CompanyUserFilters) => {
    const response = await companyUserService.getCompanyUsers(filters);
    return response;
  }
);

export const fetchCompanyUser = createAsyncThunk(
  'companyUsers/fetchCompanyUser',
  async (id: string) => {
    const response = await companyUserService.getCompanyUser(id);
    return response.data;
  }
);

export const fetchUsersByCompany = createAsyncThunk(
  'companyUsers/fetchUsersByCompany',
  async (companyId: string) => {
    const response = await companyUserService.getUsersByCompany(companyId);
    return response.data;
  }
);

// Fetch limited company users with caching
export const fetchCompanyUsersLimited = createAsyncThunk(
  'companyUsers/fetchCompanyUsersLimited',
  async ({ forceRefresh = false }: { forceRefresh?: boolean } = {}, { getState, rejectWithValue }) => {
    const state = getState() as { companyUsers: CompanyUsersState };
    const { limitedUsers, limitedUsersLastFetched } = state.companyUsers;

    // Cache validation: Check if data exists and is less than 5 minutes old
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
    const now = Date.now();

    if (!forceRefresh && limitedUsers.length > 0 && limitedUsersLastFetched) {
      const cacheAge = now - limitedUsersLastFetched;
      if (cacheAge < CACHE_DURATION) {
        // Return cached data
        return rejectWithValue('Using cached data');
      }
    }

    // Fetch fresh data
    const response = await companyUserService.getCompanyUsersLimited({
      isActive: true,
      limit: 100 // Get up to 100 users for dropdowns
    });

    return {
      data: response.data || [],
      timestamp: now
    };
  }
);

export const updateCompanyUser = createAsyncThunk(
  'companyUsers/updateCompanyUser',
  async ({ id, data }: { id: string; data: Partial<CompanyUser> }) => {
    const response = await companyUserService.updateCompanyUser(id, data);
    return response.data;
  }
);

export const updateUserPermissions = createAsyncThunk(
  'companyUsers/updateUserPermissions',
  async ({ id, permissions }: { id: string; permissions: string[] }) => {
    const response = await companyUserService.updateUserPermissions(id, permissions);
    return response.data;
  }
);

export const updateUserRole = createAsyncThunk(
  'companyUsers/updateUserRole',
  async ({ id, role }: { id: string; role: CompanyUser['role'] }) => {
    const response = await companyUserService.updateUserRole(id, role);
    return response.data;
  }
);

export const addPerformanceGoal = createAsyncThunk(
  'companyUsers/addPerformanceGoal',
  async ({ id, goal }: { id: string; goal: NonNullable<NonNullable<CompanyUser['performance']>['goals']>[0] }) => {
    const response = await companyUserService.addPerformanceGoal(id, goal);
    return response.data;
  }
);

export const updatePerformanceGoal = createAsyncThunk(
  'companyUsers/updatePerformanceGoal',
  async ({ id, goalId, updates }: { id: string; goalId: string; updates: Partial<NonNullable<NonNullable<CompanyUser['performance']>['goals']>[0]> }) => {
    const response = await companyUserService.updatePerformanceGoal(id, goalId, updates);
    return response.data;
  }
);

export const deactivateCompanyUser = createAsyncThunk(
  'companyUsers/deactivateCompanyUser',
  async (id: string) => {
    const response = await companyUserService.deactivateCompanyUser(id);
    return response.data;
  }
);

export const reactivateCompanyUser = createAsyncThunk(
  'companyUsers/reactivateCompanyUser',
  async (id: string) => {
    const response = await companyUserService.reactivateCompanyUser(id);
    return response.data;
  }
);

export const removeCompanyUser = createAsyncThunk(
  'companyUsers/removeCompanyUser',
  async (id: string) => {
    const response = await companyUserService.removeCompanyUser(id);
    return { id, ...response };
  }
);

export const fetchCompanyUserStats = createAsyncThunk(
  'companyUsers/fetchCompanyUserStats',
  async (companyId: string) => {
    const response = await companyUserService.getCompanyUserStats(companyId);
    return response.data;
  }
);

// Invitation async thunks
export const fetchInvitationsByCompanies = createAsyncThunk(
  'companyUsers/fetchInvitationsByCompanies',
  async (companyIds: string[]) => {
    const allInvitations: Invitation[] = [];
    for (const companyId of companyIds) {
      try {
        const response = await invitationService.getCompanyInvitations(companyId, { status: 'pending' });
        if (response.data) {
          allInvitations.push(...response.data);
        }
      } catch (error) {
        console.error(`Failed to fetch invitations for company ${companyId}:`, error);
      }
    }
    return allInvitations;
  }
);

const companyUsersSlice = createSlice({
  name: 'companyUsers',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      if (action.payload !== undefined) {
        state.filters = action.payload;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    resetCompanyUsers: (state) => {
      state.companyUsers = [];
      state.currentCompanyUser = null;
      state.limitedUsers = [];
      state.limitedUsersLastFetched = null;
      state.invitations = [];
      state.pagination = initialState.pagination;
      state.filters = {};
      state.error = null;
    },
    clearInvitations: (state) => {
      state.invitations = [];
    },
    clearLimitedUsers: (state) => {
      state.limitedUsers = [];
      state.limitedUsersLastFetched = null;
    },
    setCurrentCompanyUser: (state, action) => {
      if (action.payload !== undefined) {
        state.currentCompanyUser = action.payload;
      }
    }
  },
  extraReducers: (builder) => {
    // Fetch company users
    builder
      .addCase(fetchCompanyUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCompanyUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.companyUsers = action.payload.data || [];
          if (action.payload.pagination) {
            state.pagination = action.payload.pagination;
          }
        }
      })
      .addCase(fetchCompanyUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch company users';
      })

      // Fetch single company user
      .addCase(fetchCompanyUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCompanyUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentCompanyUser = action.payload || null;
      })
      .addCase(fetchCompanyUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch company user';
      })

      // Fetch users by company
      .addCase(fetchUsersByCompany.fulfilled, (state, action) => {
        state.companyUsers = action.payload || [];
      })

      // Fetch limited company users
      .addCase(fetchCompanyUsersLimited.pending, (state) => {
        state.isLoadingLimited = true;
        state.error = null;
      })
      .addCase(fetchCompanyUsersLimited.fulfilled, (state, action) => {
        state.isLoadingLimited = false;
        state.limitedUsers = action.payload.data;
        state.limitedUsersLastFetched = action.payload.timestamp;
      })
      .addCase(fetchCompanyUsersLimited.rejected, (state, action) => {
        state.isLoadingLimited = false;
        // Don't set error for cached data rejection
        if (action.payload !== 'Using cached data') {
          state.error = action.error.message || 'Failed to fetch limited company users';
        }
      })

      // Update company user
      .addCase(updateCompanyUser.fulfilled, (state, action) => {
        if (action.payload) {
          const updatedUser = action.payload;
          const index = state.companyUsers.findIndex(u => u._id === updatedUser._id);
          if (index !== -1) {
            state.companyUsers[index] = updatedUser;
          }
          if (state.currentCompanyUser && state.currentCompanyUser._id === updatedUser._id) {
            state.currentCompanyUser = updatedUser;
          }
        }
      })

      // Update user permissions
      .addCase(updateUserPermissions.fulfilled, (state, action) => {
        if (action.payload) {
          const updatedUser = action.payload;
          const index = state.companyUsers.findIndex(u => u._id === updatedUser._id);
          if (index !== -1) {
            state.companyUsers[index] = updatedUser;
          }
          if (state.currentCompanyUser && state.currentCompanyUser._id === updatedUser._id) {
            state.currentCompanyUser = updatedUser;
          }
        }
      })

      // Update user role
      .addCase(updateUserRole.fulfilled, (state, action) => {
        if (action.payload) {
          const updatedUser = action.payload;
          const index = state.companyUsers.findIndex(u => u._id === updatedUser._id);
          if (index !== -1) {
            state.companyUsers[index] = updatedUser;
          }
          if (state.currentCompanyUser && state.currentCompanyUser._id === updatedUser._id) {
            state.currentCompanyUser = updatedUser;
          }
        }
      })

      // Add performance goal
      .addCase(addPerformanceGoal.fulfilled, (state, action) => {
        if (action.payload) {
          const updatedUser = action.payload;
          const index = state.companyUsers.findIndex(u => u._id === updatedUser._id);
          if (index !== -1) {
            state.companyUsers[index] = updatedUser;
          }
          if (state.currentCompanyUser && state.currentCompanyUser._id === updatedUser._id) {
            state.currentCompanyUser = updatedUser;
          }
        }
      })

      // Update performance goal
      .addCase(updatePerformanceGoal.fulfilled, (state, action) => {
        if (action.payload) {
          const updatedUser = action.payload;
          const index = state.companyUsers.findIndex(u => u._id === updatedUser._id);
          if (index !== -1) {
            state.companyUsers[index] = updatedUser;
          }
          if (state.currentCompanyUser && state.currentCompanyUser._id === updatedUser._id) {
            state.currentCompanyUser = updatedUser;
          }
        }
      })

      // Deactivate company user
      .addCase(deactivateCompanyUser.fulfilled, (state, action) => {
        if (action.payload) {
          const updatedUser = action.payload;
          const index = state.companyUsers.findIndex(u => u._id === updatedUser._id);
          if (index !== -1) {
            state.companyUsers[index] = updatedUser;
          }
          if (state.currentCompanyUser && state.currentCompanyUser._id === updatedUser._id) {
            state.currentCompanyUser = updatedUser;
          }
        }
      })

      // Reactivate company user
      .addCase(reactivateCompanyUser.fulfilled, (state, action) => {
        if (action.payload) {
          const updatedUser = action.payload;
          const index = state.companyUsers.findIndex(u => u._id === updatedUser._id);
          if (index !== -1) {
            state.companyUsers[index] = updatedUser;
          }
          if (state.currentCompanyUser && state.currentCompanyUser._id === updatedUser._id) {
            state.currentCompanyUser = updatedUser;
          }
        }
      })

      // Remove company user
      .addCase(removeCompanyUser.fulfilled, (state, action) => {
        if (action.payload && action.payload.id) {
          // Remove user from the array
          state.companyUsers = state.companyUsers.filter(u => u._id !== action.payload.id);

          // Clear current user if it was removed
          if (state.currentCompanyUser && state.currentCompanyUser._id === action.payload.id) {
            state.currentCompanyUser = null;
          }
        }
      })

      // Fetch company user stats
      .addCase(fetchCompanyUserStats.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          state.stats = action.payload;
        }
      })

      // Fetch invitations by companies
      .addCase(fetchInvitationsByCompanies.pending, (state) => {
        state.isLoadingInvitations = true;
      })
      .addCase(fetchInvitationsByCompanies.fulfilled, (state, action) => {
        state.isLoadingInvitations = false;
        state.invitations = action.payload;
      })
      .addCase(fetchInvitationsByCompanies.rejected, (state, action) => {
        state.isLoadingInvitations = false;
        state.error = action.error.message || 'Failed to fetch invitations';
      });
  }
});

export const {
  setFilters,
  clearError,
  resetCompanyUsers,
  setCurrentCompanyUser,
  clearInvitations,
  clearLimitedUsers
} = companyUsersSlice.actions;

export default companyUsersSlice.reducer;